import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ATT } from "../constants/testIds";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Attendance() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState({});
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));

  useEffect(() => {
    const params = { date_from: dateFrom, date_to: dateTo };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/attendance", { params }).then(({ data }) => setRows(data));
    api.get("/employees", { params: user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {} })
      .then(({ data }) => {
        const map = {};
        for (const e of data) map[e.id] = e;
        setEmployees(map);
      });
  }, [dateFrom, dateTo, tenantId, user?.role]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time & Attendance</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Attendance log</h1>
        <p className="text-sm text-zinc-500 mt-2">Team-wide log of check-ins with geo-fence status.</p>
      </div>

      <div className="border border-zinc-200 bg-white p-3 rounded-sm flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-zinc-300" data-testid="attendance-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-zinc-300" data-testid="attendance-to" />
        </div>
        <div className="text-xs text-zinc-500 ml-auto">{rows.length} records</div>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-sm">
        <table className="hr-table" data-testid={ATT.adminTable}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Employee</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Hours</th>
              <th>Status</th>
              <th>Geo</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-sm">No attendance yet for this period.</td></tr>}
            {rows.map((a) => {
              const emp = employees[a.employee_id];
              return (
                <tr key={a.id}>
                  <td className="font-mono-plex text-xs">{a.date}</td>
                  <td>
                    <div className="text-sm">{emp ? `${emp.first_name} ${emp.last_name}` : a.employee_id}</div>
                    <div className="text-xs text-zinc-500">{emp?.employee_code}</div>
                  </td>
                  <td className="font-mono-plex text-xs">{a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString() : "—"}</td>
                  <td className="font-mono-plex text-xs">{a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString() : "—"}</td>
                  <td className="font-mono-plex text-xs">{a.hours_worked ?? "—"}</td>
                  <td>
                    <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">{a.status || "present"}</Badge>
                  </td>
                  <td>
                    {a.check_in_within_geofence ? (
                      <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">On-site</Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-sm bg-orange-50 text-orange-700 border-orange-200">Off-site {a.check_in_distance_m ? `· ${Math.round(a.check_in_distance_m)}m` : ""}</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
